import React, { useState } from 'react';
import { Grid, Typography, Button, Card, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { SearchField } from '../../../components/inputs/SearchField';

interface SearchHeaderProps {
  onSearch: (term: string) => void;
  totalResults: number;
  lastSearchTerm?: string;
  isLoading?: boolean;
  hasFiltersApplied?: boolean;
  onClearFilters?: () => void;
}

export const SearchHeader: React.FC<SearchHeaderProps> = ({
  onSearch,
  totalResults,
  lastSearchTerm = '',
  isLoading = false,
  hasFiltersApplied = false,
  onClearFilters,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.code === 'Enter') {
      onSearch(searchTerm);
    }
  };

  return (
    <Card variant="default">
      <Grid item>
        <Typography component="h1" variant="h4" gutterBottom>
          {t('publicSearch.internalSearch')}
        </Typography>
        <Typography variant="body1">
          {t('publicSearch.searchDescription')}
        </Typography>
      </Grid>
      <Grid item>
        <div
          style={{
            marginTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'end',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <Box
              sx={{
                width: { xs: '200px', sm: '400px', lg: '500px' },
                flexShrink: 0,
              }}
            >
              <SearchField
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search"
                ariaLabel="Search"
                onKeyDown={handleKeyPress}
                sx={{
                  width: { xs: '200px', sm: '400px', lg: '500px' },
                  alignSelf: 'center',
                }}
              />
            </Box>
            <Button
              variant="primary"
              startIcon={<SearchIcon />}
              onClick={() => onSearch(searchTerm)}
              sx={{ my: 'auto', flexShrink: 0 }}
            >
              Search
            </Button>
          </div>
          {lastSearchTerm.trim() !== '' && (
            <Typography
              variant="body2"
              sx={{ mt: 1 }}
              role="status"
              aria-live="assertive"
              aria-atomic="true"
            >
              {isLoading
                ? t('publicSearch.loadingResults')
                : totalResults > 0
                  ? t('publicSearch.resultsLoaded', {
                      count: totalResults,
                    })
                  : t('publicSearch.noResultsFound')}
            </Typography>
          )}
          {hasFiltersApplied && onClearFilters && (
            <Button
              variant="default"
              startIcon={<ClearIcon />}
              onClick={onClearFilters}
              sx={{ ml: 2, width: 'fit-content' }}
            >
              {t('publicSearch.clearAllFilters')}
            </Button>
          )}
        </div>
      </Grid>
    </Card>
  );
};
